/** Hostname-aware routing helpers for League Stream Center. */

import { isLscAdminOnly, isLscOnlyMode } from '../config/featureFlags';

export const STREAM_CENTER_HOSTS = new Set([
  'stream.scoutlytics.gg',
  'stream.localhost',
]);

export const MAIN_SCOUTLYTICS_URL = 'https://scoutlytics.gg';

function defaultHostname(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return '';
}

function defaultPathname(): string {
  if (typeof window !== 'undefined' && window.location?.pathname) {
    return window.location.pathname;
  }
  return '';
}

export function isStreamCenterHost(hostname?: string): boolean {
  const host = (hostname ?? defaultHostname()).trim().toLowerCase();
  return STREAM_CENTER_HOSTS.has(host);
}

export function isLscFallbackPath(pathname?: string): boolean {
  const path = pathname ?? defaultPathname();
  return path === '/lsc' || path.startsWith('/lsc/');
}

export function isStudioPath(pathname?: string): boolean {
  const path = pathname ?? defaultPathname();
  const normalized = path.replace(/\/+$/, '') || '/';
  return normalized === '/studio' || path.startsWith('/studio/');
}

export function isLscSurface(hostname?: string, pathname?: string): boolean {
  const path = pathname ?? defaultPathname();
  if (isStudioPath(path)) {
    return false;
  }
  if (isLscAdminOnly()) {
    if (isLscFallbackPath(path)) {
      return true;
    }
    const normalized = path.replace(/\/+$/, '') || '/';
    if (normalized === '/') {
      return false;
    }
    return isStreamCenterHost(hostname) || isLscOnlyMode();
  }
  // LSC-only mode serves LSC at apex/www root — treat as LSC surface for head/title.
  if (isLscOnlyMode()) {
    return true;
  }
  return isStreamCenterHost(hostname) || isLscFallbackPath(pathname);
}

export function lscBasePath(hostname?: string): string {
  // Apex LSC-only uses root paths (same as stream host), not /lsc fallback.
  if (isStreamCenterHost(hostname) || isLscOnlyMode()) {
    return '';
  }
  return '/lsc';
}

function normalizeSubpath(subpath?: string): string {
  if (!subpath) {
    return '';
  }
  let path = subpath.trim();
  if (!path) {
    return '';
  }
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  if (path.length > 1 && path.endsWith('/')) {
    path = path.replace(/\/+$/, '');
  }
  return path;
}

export function lscPath(subpath?: string, hostname?: string): string {
  const base = lscBasePath(hostname);
  const normalized = normalizeSubpath(subpath);
  if (!normalized) {
    return base || '/';
  }
  if (!base) {
    return normalized;
  }
  return `${base}${normalized}`;
}

export function lscReturnTo(subpath?: string): string {
  const hostname = defaultHostname();
  if (
    isStreamCenterHost(hostname) &&
    typeof window !== 'undefined' &&
    window.location?.origin
  ) {
    return `${window.location.origin}${lscPath(subpath, hostname)}`;
  }
  return lscPath(subpath, hostname);
}

/** Link target for “Back to Scoutlytics” — absolute on stream host. */
export function mainScoutlyticsHomeUrl(hostname?: string): string {
  if (isStreamCenterHost(hostname)) {
    return MAIN_SCOUTLYTICS_URL;
  }
  return '/';
}

/**
 * Inner layout route for LscApp <Routes>.
 * Always "/" — App.tsx already mounts LscApp at /lsc/* (main) or /* (stream host).
 * Using "/lsc" here breaks matching under the /lsc/* parent (blank page).
 */
export function lscLayoutRoutePath(_hostname?: string): string {
  return '/';
}

export function lscJoinInvitePath(code: string, hostname?: string): string {
  const trimmed = code.trim();
  return lscPath(`/join/${encodeURIComponent(trimmed)}`, hostname);
}

export function buildInviteShareUrl(code: string, hostname?: string): string {
  const path = lscJoinInvitePath(code, hostname);
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

/** Canonical share URL for a published archive detail page (no internal query params). */
export function buildArchiveShareUrl(
  leagueId: string,
  archiveId: string,
  hostname?: string
): string {
  const path = lscPath(
    `/leagues/${encodeURIComponent(leagueId)}/archive/${encodeURIComponent(archiveId)}`,
    hostname
  );
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return path;
}
